import React, { useState, useEffect, useRef } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { useMinisites } from '../../hooks/useMinisites';
import { notify } from '../../components/CustomToast';
import BackButton from '../Common/BackButton';
import MinisiteCard from './MinisiteCard';
import CreateMinisiteModal from './CreateMinisiteModal';
import MinisitePreview from './MinisitePreview';
import DeleteMinisiteModal from './DeleteMinisiteModal';
import AddPagesModal from './AddPagesModal';

import './MinisiteSection.css';

const MinisiteSection = () => {
    const { setActiveView } = useDashboard();
    const {
        minisites,
        loading,
        error,
        hasMore,
        loadMore,
        createMinisite,
        updateMinisite,
        deleteMinisite,
        publishMinisite,
        syncMinisitePages,
        setMinisiteHomePage,
        updateMinisitePage,
        saveMinisitePageContent,
        saveTeam,
        deleteTeamMember,
        saveServices,
        deleteService,
        saveDocuments,
        deleteDocument,
    } = useMinisites();

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showAddPages, setShowAddPages] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [selectedMinisite, setSelectedMinisite] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Field-level validation errors from backend — passed as prop to modal so it
    // re-renders inline errors without needing a callback. Same data shape as Login.jsx:
    // { title: ["The site name is required."], contact_email: ["..."], ... }
    const [fieldErrors, setFieldErrors] = useState({});

    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Sentinel div ref — IntersectionObserver fires loadMore() when user scrolls to it
    const sentinelRef = useRef(null);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) loadMore(); },
            { threshold: 0.1 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [loadMore]);

    /* ── Create ──────────────────────────────── */
    const handleOpenCreate = () => {
        setFieldErrors({});
        setShowCreate(true);
    };

    const handleCloseCreate = () => {
        setFieldErrors({});
        setShowCreate(false);
    };

    /**
     * logo_temp_upload_id is sent in the JSON payload — modal POSTs logo
     * to /api/temp-upload on onChange and stores the returned ID.
     */
    const handleCreate = async (payload) => {
        setSubmitLoading(true);
        try {
            await createMinisite(payload);
            notify('success', 'Minisite created successfully');
            setShowCreate(false);
            setFieldErrors({});
        } catch (err) {
            if (err?.status === 422) {
                notify('error', 'Validation Error');
                setFieldErrors(err.errors || {});
            }
            // All other errors are already toasted by the global interceptor
        } finally {
            setSubmitLoading(false);
        }
    };

    /* ── Edit ────────────────────────────────── */
    const handleEdit = (minisite) => {
        setFieldErrors({});
        setSelectedMinisite(minisite);
        setShowEdit(true);
    };

    const handleCloseEdit = () => {
        setFieldErrors({});
        setShowEdit(false);
        setSelectedMinisite(null);
    };

    const handleEditSubmit = async (payload) => {
        setSubmitLoading(true);
        try {
            await updateMinisite(selectedMinisite.id, payload);
            notify('success', 'Minisite updated successfully');
            setShowEdit(false);
            setSelectedMinisite(null);
            setFieldErrors({});
        } catch (err) {
            if (err?.status === 422) {
                notify('error', 'Validation Error');
                setFieldErrors(err.errors || {});
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    /* ── Add Page (opens Add Pages Modal) ───── */
    const handleAddPage = (minisite) => {
        setSelectedMinisite(minisite);
        setShowAddPages(true);
    };

    const handleStatusChange = async (pagesData) => {
        setSubmitLoading(true);
        try {
            const response = await syncMinisitePages(selectedMinisite.id, pagesData);
            setSelectedMinisite(response.data);
            notify('success', 'Page status updated');
        } catch (err) {
            notify('error', err?.message || 'Failed to update page status');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleTogglePublish = async () => {
        setSubmitLoading(true);
        try {
            const response = await publishMinisite(selectedMinisite.id);
            setSelectedMinisite(response.data);
            notify('success', response.data.status === 'published' ? 'Minisite Published' : 'Minisite set to Draft');
        } catch (err) {
            notify('error', err?.message || 'Failed to toggle status');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSetHome = async (pageId) => {
        setSubmitLoading(true);
        try {
            const response = await setMinisiteHomePage(selectedMinisite.id, pageId);
            setSelectedMinisite(response.data);
            notify('success', 'Home page set successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to set home page');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdateContent = async (pageId, content) => {
        try {
            await updateMinisitePage(selectedMinisite.id, pageId, { content });
            // Since updateMinisitePage updates the context state directly, 
            // the `minisites` array holds the updated page.
            // But AddPagesModal uses `selectedMinisite`, so we need to sync it.
            setSelectedMinisite(prev => ({
                ...prev,
                pages: prev.pages.map(p => p.id === pageId ? { ...p, content } : p)
            }));
            notify('success', 'Content saved');
        } catch (err) {
            notify('error', err?.message || 'Failed to save content');
        }
    };

    const handleSaveContent = async (pageId, content) => {
        try {
            await saveMinisitePageContent(selectedMinisite.id, pageId, content);
            notify('success', 'Content saved successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to save content');
        }
    };

    const handleSaveTeam = async (pageId, formData) => {
        try {
            const response = await saveTeam(selectedMinisite.id, pageId, formData);

            // Wait, response.data has the updated MinisitePageResource which contains the new .content
            const newContent = response?.data?.content;

            if (newContent) {
                setSelectedMinisite(prev => ({
                    ...prev,
                    pages: prev.pages.map(p => p.id === pageId ? { ...p, content: newContent } : p)
                }));
            }

            notify('success', 'Team saved successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to save team');
            throw err; // So the modal can handle loading state if necessary
        }
    };

    const handleDeleteTeamMember = async (pageId, memberId) => {
        try {
            const response = await deleteTeamMember(selectedMinisite.id, pageId, memberId);

            // Wait, response.data has the updated MinisitePageResource which contains the new .content
            const newContent = response?.data?.content;

            if (newContent) {
                setSelectedMinisite(prev => ({
                    ...prev,
                    pages: prev.pages.map(p => p.id === pageId ? { ...p, content: newContent } : p)
                }));
            }

            notify('delete', 'Team member removed successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to remove team member');
            throw err;
        }
    };

    const handleSaveServices = async (pageId, formData) => {
        try {
            const response = await saveServices(selectedMinisite.id, pageId, formData);
            const newContent = response?.data?.content;

            if (newContent) {
                setSelectedMinisite(prev => ({
                    ...prev,
                    pages: prev.pages.map(p => p.id === pageId ? { ...p, content: newContent } : p)
                }));
            }
            notify('success', 'Services saved successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to save services');
            throw err;
        }
    };

    const handleDeleteService = async (pageId, serviceId) => {
        try {
            const response = await deleteService(selectedMinisite.id, pageId, serviceId);
            const newContent = response?.data?.content;

            if (newContent) {
                setSelectedMinisite(prev => ({
                    ...prev,
                    pages: prev.pages.map(p => p.id === pageId ? { ...p, content: newContent } : p)
                }));
            }
            notify('delete', 'Service removed successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to remove service');
            throw err;
        }
    };

    const handleSaveDocuments = async (pageId, formData) => {
        try {
            const response = await saveDocuments(selectedMinisite.id, pageId, formData);
            const newContent = response?.data?.content;

            if (newContent) {
                setSelectedMinisite(prev => ({
                    ...prev,
                    pages: prev.pages.map(p => p.id === pageId ? { ...p, content: newContent } : p)
                }));
            }
            notify('success', 'Documents saved successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to save documents');
            throw err;
        }
    };

    const handleDeleteDocument = async (pageId, documentId) => {
        try {
            const response = await deleteDocument(selectedMinisite.id, pageId, documentId);
            const newContent = response?.data?.content;

            if (newContent) {
                setSelectedMinisite(prev => ({
                    ...prev,
                    pages: prev.pages.map(p => p.id === pageId ? { ...p, content: newContent } : p)
                }));
            }
            notify('delete', 'Document removed successfully');
        } catch (err) {
            notify('error', err?.message || 'Failed to remove document');
            throw err;
        }
    };

    /* ── Publish ──────────────────────────────── */
    const handlePublish = async (minisite) => {
        try {
            await publishMinisite(minisite.id);
            notify('success', 'Minisite published successfully');
        } catch (err) {
            // api.js interceptor shapes 422 as { status: 422, errors: { pages: ['msg'] } }
            if (err?.status === 422 && err?.errors?.pages) {
                notify('error', err.errors.pages[0]);
            } else {
                notify('error', err?.message || 'Failed to publish minisite');
            }
        }
    };

    /* ── Delete ───────────────────────────────── */
    const handleDeleteRequest = (minisite) => {
        setSelectedMinisite(minisite);
        setDeleteError(null);
        setConfirmDelete(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await deleteMinisite(selectedMinisite.id);
            notify('delete', 'Minisite deleted successfully');
            setConfirmDelete(false);
            setSelectedMinisite(null);
        } catch (err) {
            setDeleteError(err?.message || 'Failed to delete minisite');
        } finally {
            setDeleteLoading(false);
        }
    };

    /* ── Preview ──────────────────────────────── */
    const handleView = (minisite) => {
        setSelectedMinisite(minisite);
        setShowPreview(true);
    };

    /* ── Render ───────────────────────────────── */
    return (
        <div className="minisite-section">

            {/* Header */}
            <div className="ms-header">
                <BackButton onClick={() => setActiveView('dashboard')} />
                <h1>Minisite</h1>
            </div>

            {/* Card grid */}
            <div className="ms-body">
                <div className="ms-grid">

                    {/* Create New Minisite card — always first */}
                    <button className="ms-card-create" onClick={handleOpenCreate}>
                        <div className="ms-card-create-circle">+</div>
                        <span className="ms-card-create-label">Create New Minisite</span>
                    </button>

                    {/* Loading skeletons */}
                    {loading && minisites.length === 0 && (
                        Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="ms-card ms-card-skeleton">
                                <div className="ms-skeleton ms-skeleton-title" />
                                <div className="ms-skeleton ms-skeleton-sub" />
                                <div className="ms-skeleton ms-skeleton-btns" />
                            </div>
                        ))
                    )}

                    {/* API error state — hidden for now, will be handled later */}

                    {/* Real minisite cards */}
                    {minisites.map((minisite) => (
                        <MinisiteCard
                            key={minisite.id}
                            minisite={minisite}
                            onView={handleView}
                            onEdit={handleEdit}
                            onPublish={handlePublish}
                            onDelete={handleDeleteRequest}
                            onAddPage={handleAddPage}
                            onViewDetails={handleAddPage}
                            isOwner={minisite.is_owner}
                        />
                    ))}

                    {/* Scroll sentinel — triggers loadMore() when scrolled into view */}
                    <div ref={sentinelRef} className="ms-scroll-sentinel">
                        {loading && hasMore && (
                            <div className="ms-sentinel-spinner">
                                <span className="ms-sentinel-dot" />
                                <span className="ms-sentinel-dot" />
                                <span className="ms-sentinel-dot" />
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {/* Create modal */}
            {showCreate && (
                <CreateMinisiteModal
                    onClose={handleCloseCreate}
                    onSubmit={handleCreate}
                    loading={submitLoading}
                    fieldErrors={fieldErrors}
                />
            )}

            {/* Edit modal */}
            {showEdit && selectedMinisite && (
                <CreateMinisiteModal
                    onClose={handleCloseEdit}
                    onSubmit={handleEditSubmit}
                    initialData={selectedMinisite}
                    isEdit
                    loading={submitLoading}
                    fieldErrors={fieldErrors}
                />
            )}

            {/* Preview */}
            {showPreview && selectedMinisite && (
                <MinisitePreview
                    minisite={selectedMinisite}
                    onClose={() => { setShowPreview(false); setSelectedMinisite(null); }}
                    onPublish={handlePublish}
                    isOwner={selectedMinisite.is_owner}
                />
            )}

            {/* Add Pages */}
            <AddPagesModal
                isOpen={showAddPages}
                minisite={selectedMinisite}
                onClose={() => { setShowAddPages(false); setSelectedMinisite(null); }}
                onTogglePublish={handleTogglePublish}
                onStatusChange={handleStatusChange}
                onSetHome={handleSetHome}
                onSaveContent={handleSaveContent}
                onSaveTeam={handleSaveTeam}
                onDeleteTeamMember={handleDeleteTeamMember}
                onSaveServices={handleSaveServices}
                onDeleteService={handleDeleteService}
                onSaveDocuments={handleSaveDocuments}
                onDeleteDocument={handleDeleteDocument}
                loading={submitLoading}
            />

            {/* Confirm delete */}
            <DeleteMinisiteModal
                isOpen={confirmDelete}
                onClose={() => { setConfirmDelete(false); setSelectedMinisite(null); setDeleteError(null); }}
                onConfirm={handleDeleteConfirm}
                minisite={selectedMinisite}
                loading={deleteLoading}
                error={deleteError}
            />
        </div>
    );
};

export default MinisiteSection;
